import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Bell,
  LogOut,
  Search,
  Settings,
  HelpCircle,
  ChevronDown,
  Command,
  Mail,
  AlertCircle,
  CheckCircle,
  X,
  ExternalLink,
  LayoutDashboard,
  Send,
  Inbox,
  BarChart3,
  Users,
  FileText,
  HardDrive,
  User,
  Building,
} from 'lucide-react'
import { useAuthStore } from '@stores/auth'
import { useNavigate, useLocation } from 'react-router-dom'
import { cn } from '@lib/utils'
import { clientService } from '@services/client.service'
import type { Client } from '@/types'

// Page titles mapping
const pageTitles: Record<string, { title: string; subtitle: string }> = {
  '/dashboard': { title: 'Dashboard', subtitle: 'Overview & Analytics' },
  '/campaigns': { title: 'Campaigns', subtitle: 'Email Campaigns' },
  '/clients': { title: 'Clients', subtitle: 'Contact Management' },
  '/templates': { title: 'Templates', subtitle: 'Email Templates' },
  '/mailboxes': { title: 'Mailboxes', subtitle: 'SMTP Configuration' },
  '/queue': { title: 'Queue', subtitle: 'Job Status' },
  '/settings': { title: 'Settings', subtitle: 'App Configuration' },
  '/whatsapp': { title: 'WhatsApp', subtitle: 'Messaging Platform' },
}

// Get page info from path
const getPageInfo = (pathname: string) => {
  if (pageTitles[pathname]) return pageTitles[pathname]
  for (const [path, info] of Object.entries(pageTitles)) {
    if (pathname.startsWith(path)) return info
  }
  return { title: 'VelesMass', subtitle: 'Enterprise Email Platform' }
}

// User dropdown menu
const UserDropdown = ({
  user,
  onLogout,
  onSettings,
  isOpen,
  onClose,
}: {
  user: any
  onLogout: () => void
  onSettings: () => void
  isOpen: boolean
  onClose: () => void
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose()
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={dropdownRef}
          initial={{ opacity: 0, y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="absolute right-0 top-full mt-2 w-64 origin-top-right z-50"
        >
          <div className="bg-[#1a0f2e] border border-violet-500/20 rounded-2xl overflow-hidden shadow-xl shadow-violet-500/10">
            {/* User info */}
            <div className="p-4 border-b border-violet-500/10">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-semibold text-lg shadow-lg shadow-violet-500/30">
                  {user?.name?.[0] || user?.email?.[0] || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-100 truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-sm text-violet-300/60 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Menu items */}
            <div className="p-2">
              <button
                onClick={() => { onSettings(); onClose(); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-300 hover:bg-violet-500/10 hover:text-violet-300 transition-colors"
              >
                <Settings size={18} />
                <span>Account Settings</span>
              </button>
              <a
                href="https://veles.digital"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-300 hover:bg-violet-500/10 hover:text-violet-300 transition-colors"
              >
                <HelpCircle size={18} />
                <span>Help & Support</span>
                <ExternalLink size={14} className="ml-auto text-violet-400/50" />
              </a>
            </div>

            {/* Logout */}
            <div className="p-2 border-t border-violet-500/10">
              <button
                onClick={onLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-rose-400 hover:bg-rose-500/10 transition-colors"
              >
                <LogOut size={18} />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// Notification dropdown
const NotificationDropdown = ({
  isOpen,
  onClose,
  notifications,
  onMarkAllRead,
}: {
  isOpen: boolean
  onClose: () => void
  notifications: Array<{ id: number; type: 'success' | 'error' | 'info'; title: string; message: string; time: string }>
  onMarkAllRead: () => void
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose()
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  const getIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckCircle size={18} className="text-emerald-400" />
      case 'error': return <AlertCircle size={18} className="text-rose-400" />
      default: return <Mail size={18} className="text-violet-400" />
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={dropdownRef}
          initial={{ opacity: 0, y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="absolute right-0 top-full mt-2 w-80 origin-top-right z-50"
        >
          <div className="bg-[#1a0f2e] border border-violet-500/20 rounded-2xl overflow-hidden shadow-xl shadow-violet-500/10">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-violet-500/10">
              <h3 className="font-semibold text-slate-100">Notifications</h3>
              <button
                onClick={onClose}
                className="p-1 rounded-lg text-slate-400 hover:text-violet-300 hover:bg-violet-500/10 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Notifications list */}
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-6 text-center">
                  <Bell size={32} className="mx-auto mb-2 text-violet-500/30" />
                  <p className="text-sm text-slate-400">No new notifications</p>
                </div>
              ) : (
                <div className="divide-y divide-violet-500/10">
                  {notifications.map((notif) => (
                    <div key={notif.id} className="p-4 hover:bg-violet-500/5 transition-colors cursor-pointer">
                      <div className="flex gap-3">
                        {getIcon(notif.type)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-200">{notif.title}</p>
                          <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{notif.message}</p>
                          <p className="text-xs text-violet-400/50 mt-1">{notif.time}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-3 border-t border-violet-500/10">
                <button
                  onClick={onMarkAllRead}
                  className="w-full py-2 text-sm text-violet-400 hover:text-violet-300 transition-colors"
                >
                  Mark all as read
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// Notification badge
const NotificationBadge = ({ count }: { count: number }) => (
  <motion.div
    initial={{ scale: 0 }}
    animate={{ scale: 1 }}
    className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-violet-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg shadow-violet-500/30"
  >
    <span className="text-[10px] font-bold text-white">
      {count > 9 ? '9+' : count}
    </span>
  </motion.div>
)

// Search command hint
const SearchHint = () => (
  <div className="hidden md:flex items-center gap-1 px-2 py-1 bg-violet-500/10 rounded-lg text-xs text-violet-400">
    <Command size={12} />
    <span>K</span>
  </div>
)

// Navigation pages for search
const navPages = [
  { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, keywords: ['home', 'overview', 'analytics'] },
  { name: 'Campaigns', path: '/campaigns', icon: Send, keywords: ['email', 'send', 'campaign', 'sequence'] },
  { name: 'Inbox', path: '/inbox', icon: Inbox, keywords: ['inbox', 'replies', 'responses', 'messages'] },
  { name: 'Tracking', path: '/tracking', icon: BarChart3, keywords: ['tracking', 'status', 'monitor', 'responders'] },
  { name: 'Clients', path: '/clients', icon: Users, keywords: ['clients', 'contacts', 'subscribers', 'leads'] },
  { name: 'Templates', path: '/templates', icon: FileText, keywords: ['templates', 'email', 'content'] },
  { name: 'Mailboxes', path: '/mailboxes', icon: HardDrive, keywords: ['mailboxes', 'smtp', 'imap', 'config'] },
  { name: 'Settings', path: '/settings', icon: Settings, keywords: ['settings', 'configuration', 'account'] },
]

// Search Modal
const SearchModal = ({
  isOpen,
  onClose,
  onNavigate,
}: {
  isOpen: boolean
  onClose: () => void
  onNavigate: (path: string) => void
}) => {
  const [query, setQuery] = useState('')
  const [clientResults, setClientResults] = useState<Client[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Filter nav pages by query
  const filteredPages = useMemo(() => {
    if (!query.trim()) return navPages
    const q = query.toLowerCase()
    return navPages.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.keywords.some((k) => k.includes(q))
    )
  }, [query])

  // Total results count
  const totalResults = filteredPages.length + clientResults.length

  // Debounced client search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!query.trim() || query.length < 2) {
      setClientResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await clientService.fetchClients({ search: query, limit: 5 })
        setClientResults(res.data || [])
      } catch {
        setClientResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query])

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setClientResults([])
      setSelectedIndex(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [isOpen])

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [filteredPages.length, clientResults.length])

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((i) => (i + 1) % Math.max(totalResults, 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((i) => (i - 1 + Math.max(totalResults, 1)) % Math.max(totalResults, 1))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (selectedIndex < filteredPages.length) {
          onNavigate(filteredPages[selectedIndex].path)
          onClose()
        } else {
          const clientIdx = selectedIndex - filteredPages.length
          if (clientResults[clientIdx]) {
            onNavigate('/clients')
            onClose()
          }
        }
      }
    },
    [selectedIndex, filteredPages, clientResults, totalResults, onNavigate, onClose]
  )

  if (!isOpen) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed top-[20%] left-1/2 -translate-x-1/2 z-50 w-full max-w-lg"
          >
            <div className="bg-[#1a0f2e]/95 backdrop-blur-xl border border-violet-500/20 rounded-2xl overflow-hidden shadow-2xl shadow-violet-500/10">
              {/* Search input */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-violet-500/10">
                <Search size={18} className="text-violet-400 flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search pages, clients..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 bg-transparent text-slate-100 text-sm placeholder:text-slate-500 outline-none"
                />
                <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-violet-500/10 rounded text-[10px] text-violet-400 border border-violet-500/20">
                  ESC
                </kbd>
              </div>

              {/* Results */}
              <div className="max-h-80 overflow-y-auto py-2">
                {/* Pages */}
                {filteredPages.length > 0 && (
                  <div>
                    <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Pages
                    </div>
                    {filteredPages.map((page, idx) => {
                      const Icon = page.icon
                      return (
                        <button
                          key={page.path}
                          onClick={() => {
                            onNavigate(page.path)
                            onClose()
                          }}
                          onMouseEnter={() => setSelectedIndex(idx)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                            selectedIndex === idx
                              ? 'bg-violet-500/15 text-violet-200'
                              : 'text-slate-300 hover:bg-violet-500/10'
                          )}
                        >
                          <Icon size={16} className="text-violet-400 flex-shrink-0" />
                          <span>{page.name}</span>
                        </button>
                      )
                    })}
                  </div>
                )}

                {/* Client results */}
                {(clientResults.length > 0 || isSearching) && (
                  <div>
                    <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 mt-1">
                      Clients {isSearching && '...'}
                    </div>
                    {clientResults.map((client, idx) => {
                      const resultIdx = filteredPages.length + idx
                      return (
                        <button
                          key={client.id}
                          onClick={() => {
                            onNavigate('/clients')
                            onClose()
                          }}
                          onMouseEnter={() => setSelectedIndex(resultIdx)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                            selectedIndex === resultIdx
                              ? 'bg-violet-500/15 text-violet-200'
                              : 'text-slate-300 hover:bg-violet-500/10'
                          )}
                        >
                          <User size={16} className="text-violet-400 flex-shrink-0" />
                          <div className="flex-1 text-left min-w-0">
                            <div className="truncate">{client.fullName || client.email}</div>
                            {client.company && (
                              <div className="flex items-center gap-1 text-xs text-slate-500 truncate">
                                <Building size={10} />
                                {client.company}
                              </div>
                            )}
                          </div>
                          <span className={cn(
                            'text-[10px] px-1.5 py-0.5 rounded-full border',
                            client.status === 'RESPONDED'
                              ? 'text-emerald-400 border-emerald-500/20 bg-emerald-500/10'
                              : 'text-slate-400 border-slate-600/30 bg-slate-600/10'
                          )}>
                            {client.status}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}

                {/* No results */}
                {query.trim() && filteredPages.length === 0 && clientResults.length === 0 && !isSearching && (
                  <div className="px-4 py-8 text-center">
                    <Search size={24} className="mx-auto mb-2 text-violet-500/30" />
                    <p className="text-sm text-slate-500">No results found</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export function Header() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  // Cmd+K / Ctrl+K global shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
      if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [searchOpen])

  const [notifications, setNotifications] = useState([
    { id: 1, type: 'success' as const, title: 'Campaign Completed', message: 'Your "Welcome Series" campaign has finished sending.', time: '5 min ago' },
    { id: 2, type: 'info' as const, title: 'New Subscribers', message: '24 new clients imported successfully.', time: '1 hour ago' },
    { id: 3, type: 'error' as const, title: 'Delivery Issue', message: '3 emails failed to deliver. Check your mailbox settings.', time: '2 hours ago' },
  ])

  const handleMarkAllRead = () => {
    setNotifications([])
    setNotificationDropdownOpen(false)
  }

  const pageInfo = getPageInfo(location.pathname)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleSettings = () => {
    navigate('/settings')
  }

  return (
    <motion.header
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        'sticky top-0 z-30 h-16',
        'bg-[#0a0118]/90 backdrop-blur-xl border-b border-violet-500/10',
        'flex items-center justify-between px-6'
      )}
    >
      {/* Left side - Page title */}
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="flex flex-col"
      >
        <h1 className="text-lg font-semibold text-slate-100">
          {pageInfo.title}
        </h1>
        <p className="text-sm text-violet-400/60">
          {pageInfo.subtitle}
        </p>
      </motion.div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-2">
        {/* Search */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setSearchOpen(true)}
          className={cn(
            'hidden sm:flex items-center gap-3 px-4 py-2.5',
            'bg-violet-500/5 hover:bg-violet-500/10',
            'rounded-xl text-sm text-slate-400',
            'transition-all duration-200 border border-violet-500/10',
            'min-w-[200px]'
          )}
        >
          <Search size={16} className="text-violet-400" />
          <span className="flex-1 text-left">Search...</span>
          <SearchHint />
        </motion.button>

        {/* Notifications */}
        <div className="relative">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setNotificationDropdownOpen(!notificationDropdownOpen)
              setUserDropdownOpen(false)
            }}
            className={cn(
              'p-2.5 rounded-xl text-violet-400 transition-all duration-200',
              'hover:bg-violet-500/10 hover:text-violet-300',
              notificationDropdownOpen && 'bg-violet-500/10'
            )}
            aria-label="Notifications"
          >
            <Bell size={20} />
            {notifications.length > 0 && <NotificationBadge count={notifications.length} />}
          </motion.button>

          <NotificationDropdown
            isOpen={notificationDropdownOpen}
            onClose={() => setNotificationDropdownOpen(false)}
            notifications={notifications}
            onMarkAllRead={handleMarkAllRead}
          />
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-violet-500/10 mx-2" />

        {/* User menu */}
        <div className="relative">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setUserDropdownOpen(!userDropdownOpen)
              setNotificationDropdownOpen(false)
            }}
            className={cn(
              'flex items-center gap-3 pl-2 pr-3 py-1.5',
              'rounded-xl transition-all duration-200',
              'hover:bg-violet-500/10',
              userDropdownOpen && 'bg-violet-500/10'
            )}
          >
            {/* Avatar */}
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-medium text-sm shadow-lg shadow-violet-500/20">
              {user?.name?.[0] || user?.email?.[0] || 'U'}
            </div>

            {/* Name */}
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium text-slate-200 leading-tight">
                {user?.name || 'User'}
              </p>
            </div>

            {/* Chevron */}
            <motion.div
              animate={{ rotate: userDropdownOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown size={16} className="text-violet-400" />
            </motion.div>
          </motion.button>

          <UserDropdown
            user={user}
            onLogout={handleLogout}
            onSettings={handleSettings}
            isOpen={userDropdownOpen}
            onClose={() => setUserDropdownOpen(false)}
          />
        </div>
      </div>

      {/* Search Modal */}
      <SearchModal
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        onNavigate={(path) => navigate(path)}
      />
    </motion.header>
  )
}
